import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import OtpInput from "../components/OtpInput";
import { motion } from "framer-motion";
import { API_BASE_URL } from "../config";
import axios from "axios";
import useTitle from "../hooks/useTitle";
import { toast } from "react-toastify";

export default function Login() {
  useTitle("Login | Astro Portal");
  const [contact, setContact] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [sentOtp, setSentOtp] = useState(""); // OTP from API
  const [otpSent, setOtpSent] = useState(false);
  const [errors, setErrors] = useState({});
  const [showLogin, setShowLogin] = useState(true);
  const [userEmail, setUserEmail] = useState(null);
  const [userType, setUserType] = useState("user");
  const [showRegister, setShowRegister] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const verifyOtp = async () => {
    setIsLoading(true);
    try {
      await handleOtpVerify(); // API call
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };


  // ----------------- Form Validation -----------------
  const validateForm = () => {
    let newErrors = {};
    if (!contact) newErrors.contact = "Email or Phone is required!";
    else if (!/^\S+@\S+\.\S+$/.test(contact) && !/^\d{10}$/.test(contact))
      newErrors.contact = "Enter a valid email or 10-digit phone number";

    if (!password) newErrors.password = "Password is required!";
    else if (password.length < 6)
      newErrors.password = "Password must be at least 6 characters";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ----------------- LOGIN (Step 1) -----------------
  const handleLoginApi = async () => {
    if (!validateForm()) return;

    if (isSendingOtp) return; // ✅ block multiple clicks
    setIsSendingOtp(true);

    try {
      const { data } = await axios.post(`${API_BASE_URL}/auth/login`, {
        type: userType,
        password: password,
        ...(contact.includes("@")
          ? { email: contact }
          : { phone_number: contact }),
      });

      if (!data.status) {
        toast.error(data.message || "Invalid credentials!");
        return;
      }

      // ✅ OTP sent successfully → store email + OTP expiry
      setUserEmail(contact);
      setOtpSent(true);

      if (data.login_otp_expires_at) {
        sessionStorage.setItem("otp_expiry", data.login_otp_expires_at);
      }
      toast.success(data.message || "OTP sent to your email/phone!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Login failed!");
    } finally {
      setIsSendingOtp(false); // ✅ stop loader
    }
  };

  // ----------------- OTP VERIFICATION (Step 2) -----------------
  const handleOtpVerify = async () => {
    if (!otp.trim()) {
      toast.warning("Please enter OTP!");
      return;
    }

    try {
      const { data } = await axios.post(`${API_BASE_URL}/auth/verify-otp`, {
        type: userType,
        email: userEmail,
        otp: otp,
      });

      if (!data.status) {
        toast.error(data.message || "Invalid OTP!");
        return;
      }

      // ✅ OTP verified → save session data
      const sessionData = {
        user_id: data.data.id,
        role: data.data.role || "user", // if backend sends role, use it
        jwt_token: data.access_token,
        token_type: data.token_type,
        token_expires_at: data.expires_at,
        user: data.data,
      };

      sessionStorage.clear();
      sessionStorage.setItem("authSession", JSON.stringify(sessionData));
      toast.success(data.message || "Login successful!");
      setShowLogin(false);

      setTimeout(() => {
        if (sessionData.role === "user") navigate("/");
        else navigate("/"); // change path for astrologer/admin if needed
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.message || "OTP verification failed!");
    }
  };

  // ----------------- OTP Countdown -----------------
  const [otpTimeLeft, setOtpTimeLeft] = useState(0);

  useEffect(() => {
    const expiry = sessionStorage.getItem("otp_expiry");
    if (!expiry) return;

    const expiryTime = new Date(expiry).getTime();

    const interval = setInterval(() => {
      const remaining = expiryTime - Date.now();
      if (remaining <= 0) {
        setOtpTimeLeft(0);
        clearInterval(interval);
      } else {
        setOtpTimeLeft(Math.floor(remaining / 1000));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [otpSent]);

  // ----------------- Token Expiry Check -----------------
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     const session = JSON.parse(sessionStorage.getItem("authSession"));
  //     if (
  //       session &&
  //       new Date(session.token_expires_at).getTime() <= Date.now()
  //     ) {
  //       sessionStorage.removeItem("authSession");
  //       alert("Session expired. Please login again.");
  //       navigate("/login");
  //     }
  //   }, 60000);

  //   return () => clearInterval(interval);
  // }, [navigate]);

  const handleResendOtp = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/resend-otp`, {
        type: userType,
        email: userEmail,
      });

      if (response.data.status) {
        toast.success(response.data.message || "OTP sent to your email/phone!");

        // 🔹 set new expiry time = now + 600 seconds
        const newExpiry = new Date(Date.now() + 600 * 1000).toISOString();
        sessionStorage.setItem("otp_expiry", newExpiry);

        // 🔹 update state immediately (so UI updates without waiting for interval)
        setOtpTimeLeft(newExpiry - Date.now());
      } else {
        toast.error(response.data.message || "Failed to resend OTP");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      toast.error("Something went wrong while resending OTP");
    }
  };

  return (
    <>
      {showLogin && (
        <div className="d-flex justify-content-center align-items-center bg-light py-5">
          <div
            className="row border-0 rounded-5 shadow-lg p-4"
            style={{
              maxWidth: "900px",
              background: "linear-gradient(145deg, #ffffff, #f0f0f0)",
              boxShadow:
                "10px 10px 25px rgba(0,0,0,0.15), -10px -10px 25px rgba(255,255,255,0.8)",
              transform: "translateY(0)",
              transition: "all 0.3s ease-in-out",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px) scale(1.01)";
              e.currentTarget.style.boxShadow =
                "15px 15px 35px rgba(0,0,0,0.2), -15px -15px 35px rgba(255,255,255,0.9)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow =
                "10px 10px 25px rgba(0,0,0,0.15), -10px -10px 25px rgba(255,255,255,0.8)";
            }}
          >
            <motion.div
              initial={{ x: -80, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="col-md-6 px-4 py-0 d-flex flex-column justify-content-center"
            >
              <h2 className="mb-4 text-center fw-bold text-gradient">Login</h2>

              {!otpSent ? (
                <>
                  {/* Contact */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-secondary">
                      Email or Phone
                    </label>
                    <motion.input
                      whileFocus={{ scale: 1.02 }}
                      type="text"
                      placeholder="Enter your email or phone"
                      className={`form-control form-control-lg fs-6 rounded-pill shadow-sm ${errors.contact ? "is-invalid" : ""
                        }`}
                      style={{
                        boxShadow:
                          "inset 4px 4px 8px #c9c9c9, inset -4px -4px 8px #ffffff",
                      }}
                      value={contact}
                      onChange={(e) => setContact(e.target.value)}
                    />
                    {errors.contact && (
                      <div className="invalid-feedback">{errors.contact}</div>
                    )}
                  </div>

                  {/* Password */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-secondary">
                      Password
                    </label>
                    <motion.input
                      whileFocus={{ scale: 1.02 }}
                      type="password"
                      placeholder="Enter your password"
                      className={`form-control form-control-lg fs-6 rounded-pill shadow-sm ${errors.password ? "is-invalid" : ""
                        }`}
                      style={{
                        boxShadow:
                          "inset 4px 4px 8px #c9c9c9, inset -4px -4px 8px #ffffff",
                      }}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    {errors.password && (
                      <div className="invalid-feedback">{errors.password}</div>
                    )}
                  </div>

                  {/* Send OTP Button */}
                  <motion.button
                    whileHover={!isSendingOtp ? { scale: 1.05, y: -2 } : {}}
                    whileTap={!isSendingOtp ? { scale: 0.95, y: 2 } : {}}
                    onClick={handleLoginApi}
                    disabled={isSendingOtp}
                    className="btn btn-lg w-100 fw-bold rounded-pill d-flex justify-content-center align-items-center"
                    style={{
                      background: "linear-gradient(145deg, #4facfe, #00f2fe)",
                      color: "white",
                      boxShadow: "6px 6px 12px #c9c9c9, -6px -6px 12px #ffffff",
                    }}
                  >
                    {isSendingOtp ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        />
                        Sending OTP...
                      </>
                    ) : (
                      "Send OTP"
                    )}
                  </motion.button>

                  <div className="text-center mt-3">
                    <p className="text-secondary mb-1">
                      Don’t have an account?{" "}
                      <a
                        href="/register"
                        className="fw-bold text-decoration-none text-primary"
                      >
                        Register
                      </a>
                    </p>
                  </div>
                </>
              ) : (
                <OtpInput
                  otp={otp}
                  setOtp={setOtp}
                  otpTimeLeft={otpTimeLeft}
                  onVerify={verifyOtp}
                  onResendOtp={handleResendOtp}
                  isLoading={isLoading}
                />
              )}
            </motion.div>
            {/* Right Side - Image with 3D hover */}
            <motion.div
              initial={{ x: 80, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              whileHover={{ scale: 1.02, rotateY: 5 }}
              className="col-md-6 d-none d-md-flex bg-primary text-white flex-column justify-content-center align-items-center p-5"
              style={{ perspective: "1000px" }}
            >
              <h3 className="fw-bold mb-3 text-center">Welcome to Our Store</h3>
              <p className="text-center mb-4">
                Join us and enjoy exclusive deals, quick checkout, and a
                personalized shopping experience.
              </p>
              <motion.img
                src="https://cdn-icons-png.flaticon.com/512/869/869636.png"
                alt="E-commerce illustration"
                className="img-fluid"
                style={{ maxWidth: "200px" }}
                animate={{ y: [0, -8, 0] }}
                transition={{
                  repeat: Infinity,
                  duration: 3,
                  ease: "easeInOut",
                }}
              />
            </motion.div>
          </div>
        </div>
      )}
    </>
  );
}
